import { Component, OnDestroy, OnInit, OnChanges, SimpleChanges, ViewChild, ViewEncapsulation, HostListener } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { concat, Subscription, of, from, Observable } from 'rxjs';
import { map, concatMap, switchMap, finalize } from 'rxjs/operators';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { Title } from "@angular/platform-browser";

import { environment } from 'src/environments/environment';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data';
import { ComponentCanDeactivate } from 'src/app/shared/pending-changes.guard';

import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service';
import { DocumentStackService } from 'src/app/services/document-stack.service';
import { SecurityGroups } from 'src/app/shared/securitygroups';
import { Document, DocItems, DocumentExport, ImportItemFields } from 'src/app/shared/document';
import { DocTypes } from 'src/app/shared/doc-list-entry';
import { Currency } from 'src/app/shared/currency';
import { PerListEntry } from 'src/app/shared/per-list-entry';
import { DocumentApiService } from 'src/app/services/document-api.service';
import { ClientApiService } from 'src/app/services/client-api.service';
import { ProductApiService } from 'src/app/services/product-api.service';
import { RestApiService } from 'src/app/services/rest-api.service';
import { formatNumber } from '@angular/common';

@Component({
  selector: 'app-edit-document-receipt',
  templateUrl: './edit-document-receipt.component.html',
  styleUrls: ['./edit-document-receipt.component.css'],
  encapsulation: ViewEncapsulation.None
})
/**
 * Document editor for DOCTYPE 2 (receipt), 3 (incoming), 4 (outgoing sale), and 5 (transfer).
 *
 * Follows the same full editing lifecycle as `EditDocumentComponent` but targets inbound and
 * outbound movement documents. All side and party labels (`side1Label`, `side2Label`,
 * `company1Label`, `person1Label`, `department1Label`, `lcoLabel`, `lcoDep1Label`,
 * `company2Label`, `person2Label`, `department2Label`, `pcoLabel`, `pcoDep2Label`,
 * `conditions3Label`, `conditions4Label`) switch on `_docType` to reflect the document
 * semantics for each movement type.
 *
 * Loading uses a sequential `concatMap` chain: `getDocumentById` → `getDocumentNameById`
 * → `getDocTypeById`, followed by a `finalize` snapshot of `documentCopy` for change-
 * detection. New-document mode reads from `DocumentStackService` at index 0.
 *
 * Subscription management is manual (old-style named fields). `noChangesThere` compares
 * all header properties against the copy, skipping computed line-item totals. `canDeactivate`
 * guards against navigation when there are unsaved changes or an active inline edit.
 */
export class EditDocumentReceiptComponent implements OnInit, OnDestroy, OnChanges, ComponentCanDeactivate {

  globalSettinsData: GlobalSettingsData;

  /*private*/ _docId: number = 0;
  /*private*/ _docType: number = 1;

  DOCDATE: Date;
  DOCEXPDATE: Date;
  DOCVALDATE: Date;

  private _initialQuery: Subscription;
  private _productInfoQuery: Subscription;

  private _deleteDocQuery: Subscription;
  private _childDocQuery: Subscription;

  file: any[];

  document: Document;
  documentCopy: Document;

  docTypeInfo: DocTypes;

  displayDC: boolean;

  displayND: boolean;

  displayMD: boolean;

  confirmDeleteDocumentOn: boolean = false;

  confirmDeleteDocumentText: string;
  confirmExportDocumentText: string;

  documentSecurityGroup: SecurityGroups;

  showTabRowInfo: boolean = false;
  showTabDetails: boolean = true;
  showTabSeller: boolean = true;
  showTabCustomer: boolean = true;
  showTabConditions: boolean = true;
  showTabTable: boolean = true;
  showTabTransaction: boolean = false;
  showTabSaleResult: boolean = false;

  movementIsReady: boolean = true;

  result: any[];

  resultIsReady: boolean = false;

  private _documentQuery: Subscription;
  private _documentSaveQuery: Subscription;
  private _documentPostQuery: Subscription;

  docHeaderStatement: string = '';
  docFooterStatement: string = '';

  public get side1Label(): string {
    let label: string = 'Получатель';
    switch (this._docType) {
      case 2:
      case 3:
        label = 'Получатель';
        break;

      case 4:
        label = 'Продавец';
        break;

      case 5:
        label = 'Выдача товара';
        break;

    }
    return label;
  };

  public get side2Label(): string {
    let label: string = 'Поставщик';
    switch (this._docType) {
      case 2:
      case 3:
        label = 'Поставщик';
        break;

      case 4:
        label = 'Покупатель';
        break;

      case 5:
        label = 'Прием товара';
        break;

    }
    return label;
  };

  public get company1Label(): string {
    let label: string = 'XXXX Основная организация продавца:';
    switch (this._docType) {
      case 2:
      case 3:
        label = 'Основная организация получателя:';
        break;

      case 4:
        label = 'Основная организация продавца:';
        break;

      case 5:
        label = 'Компания - поставщик:';
        break;
    }
    return label;
  };

  public get person1Label(): string {
    let label: string = 'XXXX Получатель:';
    switch (this._docType) {
      case 2:
      case 3:
        label = 'Получатель:';
        break;

      case 4:
        label = 'Контактное лицо продавца:';
        break;

      case 5:
        label = 'Ответственный за выдачу товара:';
        break;
    }
    return label;
  };

  public get department1Label(): string {
    let label: string = 'XXXX Основной склад';
    switch (this._docType) {
      case 2:
      case 3:
        label = 'Основной склад получателя';
        break;

      case 4:
        label = 'Основной склад продавца';
        break;

      case 5:
        label = 'Склад выдачи товара';
        break;
    }
    return label;
  };

  public get lcoLabel(): string {
    let label: string = 'XXXX Юридическое лицо продавца:';
    switch (this._docType) {
      case 2:
      case 3:
        label = 'Юридическое лицо получателя:';
        break;

      case 4:
        label = 'Юридическое лицо продавца:';
        break;

      case 5:
        label = 'Поставщик товара:';
        break;
    }
    return label;
  };

  public get lcoPer1Label(): string {
    let label: string = 'Ответственный представитель:';
    return label;
  };

  public get lcoDep1Label(): string {
    let label: string = 'Склад (отдел) получателя:';
    switch (this._docType) {
      case 2:
      case 3:
        label = 'Склад (отдел) получателя:';
        break;

      case 4:
      case 5:
        label = 'Склад (отдел) поставщика:';
        break;
    }
    return label;
  };

  public get company2Label(): string {
    let label: string = 'Основная организация покупателя:';
    switch (this._docType) {
      case 2:
      case 3:
        label = 'Основная организация поставщика:';
        break;

      case 4:
        label = 'Основная организация покупателя:';
        break;

      case 5:
        label = 'Компания - получатель:';
        break;
    }
    return label;
  };

  public get person2Label(): string {
    let label: string = 'Поставщик:';
    switch (this._docType) {
      case 2:
      case 3:
        label = 'Поставщик:';
        break;

      case 4:
        label = 'Покупатель:';
        break;

      case 5:
        label = 'Ответственный за прием товара:';
        break;

    }
    return label;
  };

  public get department2Label(): string {
    let label: string = 'Поставщик:';
    switch (this._docType) {
      case 2:
      case 3:
        label = 'Склад поставщика:';
        break;

      case 4:
        label = 'Склад покупателя:';
        break;

      case 5:
        label = 'Склад приема товара:';
        break;

    }
    return label;
  };

  public get pcoLabel(): string {
    let label: string = 'Юридическое лицо продавца:';
    switch (this._docType) {
      case 2:
      case 3:
        label = 'Юридическое лицо продавца:';
        break;

      case 4:
        label = 'Юридическое лицо покупателя:';
        break;

      case 5:
        label = 'Получатель товара:';
        break;

    }
    return label;
  };

  public get pcoPer2Label(): string {
    let label: string = 'Ответственный представитель:';
    return label;
  };

  public get pcoDep2Label(): string {
    let label: string = 'Склад (отдел):';
    switch (this._docType) {
      case 2:
      case 3:
        label = 'Склад (отдел) поставщика:';
        break;

      case 4:
      case 5:
        label = 'Склад (отдел) получателя:';
        break;
    }
    return label;
  };

  public get conditions1Label(): string {
    let label: string = 'Общие условия поставки';
    return label;
  };

  public get conditions2Label(): string {
    let label: string = 'Срок выполнения (поставки)';
    return label;
  };

  public get conditions3Label(): string {
    let label: string = 'XXXX Получатель:';
    switch (this._docType) {
      case 2:
      case 3:
        label = 'Реквизиты счета или другого документа:';
        break;

      case 4:
        label = 'Примечание продавца (внутренние):';
        break;

      case 5:
        label = 'Примечание при передаче товара:';
        break;

    }
    return label;
  };

  public get conditions4Label(): string {
    let label: string = 'XXX Поставщик';
    switch (this._docType) {
      case 2:
      case 3:
        label = 'Примечание получателя (для печати)';
        break;

      case 4:
        label = 'Примечание для покупателя товара';
        break;

      case 5:
        label = 'Примечание при приеме товара';
        break;

    }
    return label;
  };

  documentSpecific: DocumentExport;

  toolbarOptions: any = [
    ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
    ['blockquote', 'code-block'],

    [{ 'header': 1 }, { 'header': 2 }],               // custom button values
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'script': 'sub' }, { 'script': 'super' }],      // superscript/subscript
    [{ 'indent': '-1' }, { 'indent': '+1' }],          // outdent/indent

    [{ 'size': ['small', false, 'large', 'huge'] }],  // custom dropdown
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],

    [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults from theme
    [{ 'font': [] }],
    [{ 'align': [] }],

    ['clean']                                         // remove formatting button
  ];

  modules: any = {
    toolbar: this.toolbarOptions
  };

  /** Returns `true` when all header properties match `documentCopy`; skips computed line-item total fields. */
  public get noChangesThere(): boolean {
    let equal: boolean = true;
    if (this.document && this.documentCopy) {
      for (var props in this.document) {
        //console.log('*** EditDocumentReceiptComponent in noChangesThere: props: ' + props + '/' + this.document[props] + '/' + this.documentCopy[props])

        // Filter out document summury properties (by fact they do not modify document content)

        switch (props) {
          case 'items':
          case 'DOCITEMS':
          case 'DOCTOTALITEMS':
          case 'DOCSUMU4ETKA':
          case 'DOCVSUM':
          case 'DOCSUMWOVAT':
          case 'DOCVAT':
          case 'DOCSUM':
          case 'DOCNAMESTR':
          case 'DOCSUMPROFIT':
          //case 'DOCRATEVAL':
            break;

          default:
            equal = (this.document[props] == this.documentCopy[props]);
            break;
        }
        if (!equal) {
          //console.log('*** EditDocumentReceiptComponent in noChangesThere: ' + props + ' is ' + equal)
          break;
        }
      }
      //console.log('*** EditDocumentReceiptComponent in noChangesThere: ' + equal)
    }
    return equal;
  }

  /** Returns `true` when `document` is not yet loaded. */
  public get formInvalid(): boolean {
    return (
      !this.document
    );
  }

  /** Returns `true` when a document is loaded and has no unsaved changes (enables create-child action). */
  public get canCreate(): boolean { return (this._docId) && (this.document) && (this.noChangesThere); }

  /** Returns `true` when a document is loaded (enables delete action). */
  public get canDelete(): boolean { return (this._docId) && (this.document && true); }

  exportDS: boolean;

  exportConfirmDlg: boolean = false;

  displayImportDlg: boolean = false;

  knockKnock: boolean = false;

  importParamsMap: ImportItemFields[] = [
    { fieldName: 'PRODCODE', fieldLabel: 'Код товара', fieldIsMandatory: true, fieldType: 'str' },
    { fieldName: 'DOCITEMQTY', fieldLabel: 'Количество', fieldIsMandatory: true, fieldType: 'num' },
    { fieldName: 'DOCITEMVPRICE', fieldLabel: 'Цена в валюте', fieldIsMandatory: false, fieldType: 'num' },
    { fieldName: 'DOCITEMPRICE', fieldLabel: 'Цена товара', fieldIsMandatory: false, fieldType: 'num' },
//    { fieldName: 'U4ETKA', fieldLabel: 'Учетка', fieldIsMandatory: false, fieldType: 'num' },
    { fieldName: 'DOCITEMPRODNAME', fieldLabel: 'Наименование товара', fieldIsMandatory: false, fieldType: 'str' },
//    { fieldName: 'DOCITEMTEXT1', fieldLabel: 'Примечание 1', fieldIsMandatory: false, fieldType: 'str' },
//    { fieldName: 'DOCITEMTEXT2', fieldLabel: 'Примечание 2', fieldIsMandatory: false, fieldType: 'str' },
  ];

  editing: boolean = false;

  constructor(
    private docApi: DocumentApiService,
    //private restapi: RestApiService,
    //private prodApi: ProductApiService,
    //private cliApi: ClientApiService,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private router: Router,
    private globalsettingsservice: GetglobalsettingsService,
    private documentStack: DocumentStackService,
    private titleService: Title
  ) {
  }

  /** Guards route deactivation and browser unload; blocks navigation when there are unsaved changes or an active inline edit. */
  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    return (this.noChangesThere && !this.editing);
  }

  /** Unsubscribes from all active subscriptions to prevent memory leaks. */
  ngOnDestroy(): void {
    console.log('*** EditDocumentReceiptComponent in ngOnDestroy')

    if (this._initialQuery) { this._initialQuery.unsubscribe(); }
    if (this._documentQuery) { this._documentQuery.unsubscribe(); }
    if (this._documentSaveQuery) { this._documentSaveQuery.unsubscribe(); }
    if (this._documentPostQuery) { this._documentPostQuery.unsubscribe(); }

    if (this._productInfoQuery) { this._productInfoQuery.unsubscribe(); }
    if (this._deleteDocQuery) { this._deleteDocQuery.unsubscribe(); }
    if (this._childDocQuery) { this._childDocQuery.unsubscribe(); }
  }

  /** Logs simple-changes events (Angular lifecycle hook; no business logic). */
  ngOnChanges(changes: SimpleChanges): void {
    console.log('*** EditDocumentReceiptComponent ngOnChanges: ===> ' + JSON.stringify(changes));
  }

  /**
   * Loads the full document via a sequential `concatMap` chain:
   * `getDocumentById` → `getDocumentNameById` → `getDocTypeById`, with a `finalize`
   * snapshot of `documentCopy`. In new-document mode reads from `DocumentStackService` at index 0.
   */
  loadDocumentContent() {
    console.log('*** EditDocumentReceiptComponent loadDocumentContent: ===> Started');
    if (this._docId) {
      console.log('*** EditDocumentReceiptComponent loadDocumentContent docid: ' + this._docId);

      this._documentQuery =
        this.docApi.getDocumentById(this._docId).pipe(

          concatMap(doc => {

            this.document = doc[0];
            this.DOCDATE = new Date(this.document.DOCDATE);
            this.DOCEXPDATE = new Date(this.document.DOCEXPDATE);
            this.DOCVALDATE = new Date(this.document.DOCVALDATE);
            this._docType = this.document.DOCTYPE;

            environment.production || console.log('*** EditDocumentReceiptComponent in map loadDocumentContent: ===> ' + this.DOCDATE);

            return of(this.document);
          }),

          concatMap(doc => this.docApi.getDocumentNameById(doc.DOCID).pipe(map(data => {
            let docName: any = data[0];

            environment.production || console.log('*** EditDocumentReceiptComponent loadDocumentName: ===> doc 0: ' + JSON.stringify(docName));

            if (docName) {
              this.document.DOCNAMESTR = docName.DOCNAMESTR;
              this.titleService.setTitle(this.document.DOCNAMESTR);
            }
            return doc;
          }))),

          concatMap(doc => this.docApi.getDocTypeById(doc.DOCTYPE).pipe(map(data => {
            environment.production || console.log('*** EditDocumentReceiptComponent in get DOCTYPE: ===> ' + JSON.stringify(data));

            this.docTypeInfo = data[0];

            this.document.HeaderStatement = this.docTypeInfo.DOCTEXTBEFORE;
            this.document.FooterStatement = this.docTypeInfo.DOCTEXTAFTER;
          }))),

          finalize(() => {

            environment.production || console.log('*** EditDocumentReceiptComponent create document copy: ===> ' + JSON.stringify(this.document));
            this.documentCopy = { ... this.document };
          })

        ).subscribe();

    } else {
      /*
       * (this._prodId == 0) New Document Mode
       *
       * get Document from Document Stack
       */
      let newIndex = 0;

      /*
       * Request new Index from query string param.
       */
      this.document = this.documentStack.getDocument(newIndex);
      if (this.document) {

        this.DOCDATE = new Date(this.document.DOCDATE);
        this.DOCEXPDATE = new Date(this.document.DOCEXPDATE);
        this.DOCVALDATE = new Date(this.document.DOCVALDATE);
      }
    }
    console.log('*** EditDocumentReceiptComponent loadDocumentContent: ===> Ended');
  }

  /** Reads global settings and subscribes to `paramMap` to reload the document on route changes. */
  ngOnInit(): void {
    console.log('*** EditDocumentReceiptComponent in ngOnInit')
    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();

    this._initialQuery =
      this.route.paramMap
        .subscribe((params: ParamMap) => {
          this._docId = +params.get('id');
//          this._docType = +params.get('type');
          console.log('*** EditDocumentReceiptComponent in param subscribe: (not determined) (' + this._docId + ') ' + JSON.stringify(params));

          this.loadDocumentContent();
        });
  }

  /** Generic button-click stub (logging only). */
  buttonClick(event) {
    console.log('*** EditDocumentReceiptComponent in buttonClick: ' + JSON.stringify(event));
  }

  /** Updates `DOCDATE` and auto-computes `DOCVALDATE` / `DOCEXPDATE` from the doc-type's default terms. */
  onDateSelect(event) {
    console.log('*** EditDocumentReceiptComponent in onDateSelect: ' + JSON.stringify(event));
    let dddd = new Date(event);
    this.document.DOCDATE = dddd;

    this.document.DOCVALDATE = new Date(dddd.getFullYear(), dddd.getMonth(), dddd.getDate() + (this.docTypeInfo.DEFAULTVALTERM || 0))
    this.document.DOCEXPDATE = new Date(dddd.getFullYear(), dddd.getMonth(), dddd.getDate() + (this.docTypeInfo.DEFAULTEXPTERM || 0))

    this.DOCEXPDATE = new Date(this.document.DOCEXPDATE);
    this.DOCVALDATE = new Date(this.document.DOCVALDATE);
  }

  /** Sets `DOCEXPDATE` on the document when the expiry date picker fires. */
  onDateExpSelect(event) {
    console.log('*** EditDocumentReceiptComponent in onDateSelect: ' + JSON.stringify(event));
    this.document.DOCEXPDATE = event;
  }

  /** Sets `DOCVALDATE` on the document when the validity date picker fires. */
  onDateValSelect(event) {
    console.log('*** EditDocumentReceiptComponent in onDateSelect: ' + JSON.stringify(event));
    this.document.DOCVALDATE = event;
  }

  /** Updates `document.DOCCURRID` when the currency dropdown fires a raw ID change. */
  onDocCurrencyChange(event) {
    console.log('*** EditDocumentReceiptComponent in onDocCurrencyChange: ' + JSON.stringify(event));

    this.document.DOCCURRID = event;
  }

  /**
   * Updates `DOCCURRID` and `DOCRATEVAL` from the selected currency object. Note: this event
   * also fires during initial load, after `documentCopy` is taken — so a changed rate between
   * document creation and now may mark the header as modified without visible user action.
   */
  onCurrencySelection(event) {
    if (this.document.DOCCURRID != event.CURID) {
      this.document.DOCCURRID = event.CURID;
    }
    this.document.DOCRATEVAL = event.CURRATE;

    environment.production || console.log('*** EditDocumentReceiptComponent onCurrencySelection: ===> ' + JSON.stringify(this.document));
  }

  /** Handles selection in the recent-documents panel (logging stub). */
  onRecentRowSelect(selectedId) {
    console.log('*** EditDocumentReceiptComponent in onRecentRowSelect: ' + selectedId);
  }

  /** Logs which accordion tab was opened (stub). */
  onTabOpen(event) { console.log('*** EditDocumentReceiptComponent in onTabOpen: ' + event.index); }

  /** Sets `document.PRIMDOCID` to the selected parent's `DOCID`, or `null` if cleared. */
  parentDocumentSelected(event) {
    console.log('*** EditDocumentReceiptComponent in parentDocumentSelected: ' + JSON.stringify(event));
    this.document.PRIMDOCID = (event) ? event.DOCID : null;
  }

  /** Applies the chosen security group to `documentSecurityGroup` and writes its `SECGROUPID` to `document.SGMGROUPID`. */
  onSelectedSecurityGroup(sg: SecurityGroups) {
    console.log('*** EditDocumentReceiptComponent in onSelectedSecurityGroup: ' + JSON.stringify(sg));
    this.documentSecurityGroup = sg;
    this.document.SGMGROUPID = sg.SECGROUPID;
  }

  /** Saves the document: PUT (update) when `_docId > 0`, POST (create) when `_docId === 0`, then navigates to the new document route on success. */
  onConfirm(e: number) {
    console.log('*** EditDocumentReceiptComponent onConfirm: ' + e)

    switch (e) {
      default:
        console.log('*** EditDocumentReceiptComponent onConfirm: (Main data) ');
        environment.production || console.log('*** EditDocumentReceiptComponent buttonSaveAllClick: document ' + JSON.stringify(this.document));
        environment.production || console.log('*** EditDocumentReceiptComponent buttonSaveAllClick: documentCopy ' + JSON.stringify(this.documentCopy));

        if (this._docId) {
          this._documentSaveQuery =
            this.docApi.putDocumentById(this.document)
            .pipe(map((data) => (console.log('*** EditDocumentReceiptComponent onConfirm: *** on data map *** ' + data))))
              .subscribe(() => {
                this.documentCopy = { ... this.document };
                this.document = { ... this.document };        // Trigger the changes in child components
                console.log('*** EditDocumentReceiptComponent onConfirm: *** PUT *** ');
              });
        } else {
          this._documentPostQuery =
            this.docApi.postDocumentById(this.document)
              .pipe(map((data) => {
                console.log('*** EditDocumentReceiptComponent onConfirm: *** on Post data map *** ' + JSON.stringify(data));
                this._docId = data['DOCID'];
              }))
              .subscribe(() => {
                this.router.navigate(['/edit-document', this._docId]);
                console.log('*** EditDocumentReceiptComponent onConfirm: *** POST *** ');
              });
        }
        break;
    }
  }

  /** Shows only the conditions tab, hiding all other sections. */
  showConditions(event) {
    console.log('*** EditDocumentReceiptComponent in showConditions: ' + JSON.stringify(event));
    this.showTabRowInfo = false;
    this.showTabDetails = false;
    this.showTabSeller = false;
    this.showTabCustomer = false;
    this.showTabConditions = true;
    this.showTabTable = false;
    this.showTabTransaction = false;
    this.showTabSaleResult = false;
  }

  /** Shows only the content (items) tab, hiding all other sections. */
  showContent(event) {
    console.log('*** EditDocumentReceiptComponent in showContent: ' + JSON.stringify(event));
    this.showTabRowInfo = false;
    this.showTabDetails = false;
    this.showTabSeller = false;
    this.showTabCustomer = false;
    this.showTabConditions = false;
    this.showTabTable = true;
    this.showTabTransaction = false;
    this.showTabSaleResult = false;
  }

  /** Shows only the transactions tab, hiding all other sections. */
  showTransactions(event) {
    console.log('*** EditDocumentReceiptComponent in showTransactions: ' + JSON.stringify(event));
    this.showTabRowInfo = false;
    this.showTabDetails = false;
    this.showTabSeller = false;
    this.showTabCustomer = false;
    this.showTabConditions = false;
    this.showTabTable = false;
    this.showTabTransaction = true;
    this.showTabSaleResult = false;
  }

  /** Triggers a shallow document clone to propagate refilled content to child components. */
  onRefillDocument(event) {
    console.log('*** EditDocumentReceiptComponent onRefillDocument: ' + JSON.stringify(event))
    if (event) {
      this.document = { ...this.document };
    }
  }

  /** Opens the export overlay if no line items are highlighted; otherwise shows a confirmation dialog first. */
  onExportDocument(event) {
    console.log('*** EditDocumentReceiptComponent onRefillDocument: ' + JSON.stringify(event))
    //environment.production || console.table(this.document);
    environment.production || console.table(this.document.items);

    this.documentSpecific = {
      /*
      Owner: {
        FullNameLabel: 'Відповідальний',
        EmailLabel: 'Електронна пошта',
        PhoneLabel: 'Контактний телефон',
      },

      Header: {
        DateLabel: 'Дата',
        DocumentNameLabel: 'Назва',
        DocumentNumberLabel: 'Номер',
        ValidToLabel: 'Дійсний до',
        ExpiredOnLabel: 'Дата виконання',

        CurrencyNameLabel: 'Валюта документа',
        CurrencyRateLabel: 'Курс валюти',
        MainCurrencyNameLabel: 'Валюта документа',

      },

      Seller: {
        CompanyNameLabel: 'ПРОДАВЕЦЬ',
        CompanyIdLabel: 'Код ЄДРПОУ',
        VATIDLabel: 'Код платника ПДВ',
        VATIDDocLabel: 'Свідоцтво ПДВ',
        AccountLabel: 'Рахунок',
        BankLabel: 'Банк',
        AddressLabel: 'Адреса',
        FullNameLabel: 'Відповідальний',
        EmailLabel: 'Електронна пошта',
        PhoneLabel: 'Телефон',
      },

      Buyer: {
        CompanyNameLabel: 'ПОКУПЕЦЬ',
        CompanyIdLabel: 'Код ЄДРПОУ',
        VATIDLabel: 'Код платника ПДВ',
        VATIDDocLabel: 'Свідоцтво ПДВ',
        AccountLabel: 'Рахунок',
        BankLabel: 'Банк',
        AddressLabel: 'Адреса',
        FullNameLabel: 'Відповідальний',
        EmailLabel: 'Електронна пошта',
        PhoneLabel: 'Телефон',
      },

      ItemsLabel: {
        ItemCodeLabel: 'Код товару',
        ItemNameLabel: 'Найменування товару',
        ItemTariffCodeLabel: 'Код УКТЗЕД',
        ItemNoteLabel: 'Примітки',
        ItemPriceLabel: 'Ціна з ПДВ',
        ItemVPriceLabel: 'Ціна в валюті',
        ItemSumWoVATLabel: 'Сума без ПДВ',
        ItemPriceWoVATLabel: 'Ціна без ПДВ',
        ItemRetailPriceLabel: 'Роздріб',
        ItemRetailVPriceLabel: 'Роздріб в валюті',
        ItemDiscountLabel: 'Знижка',
        ItemQntyLabel: 'Кількість',
        ItemSumLabel: 'Сума з ПДВ',
        ItemVSumLabel: 'Сума в валюті',
        ItemVATLabel: 'ПДВ',

        ItemVendorPriceLabel: 'ItemVendorPriceLabel',
        ItemInvoicePriceLabel: 'ItemInvoicePriceLabel',
        ItemInvoiceSumLabel: 'ItemInvoiceSumLabel',

        ItemIncomePriceLabel: 'ItemIncomePriceLabel',
        ItemIncomeSumLabel: 'ItemIncomeSumLabel',

        ItemProdWeightLabel: 'ItemProdWeightLabel',
        ItemProdWeightSumLabel: 'ItemProdWeightSumLabel',

      },

      Conditions: {
        DeliveryConditionsLabel: 'Умови постачання',
        DeliveryTermsLabel: 'Очікуванний термін виконаня (днів)',
        DocumentNoteLabel: 'Примітки документа',
      },

      Summary: {
        TotalSumLabel: 'Загалом, з ПДВ',
        TotalVSumLabel: 'Загалом, з ПДВ, в валюті',
        TotalSumWithOutVATLabel: 'Сума без ПДВ',
        TotalSumVATLabel: 'Сума ПДВ',
        TotalItemsLabel: 'Усього найменувань',
        TotalQuantityLabel: 'Загальна кількість',
        TotalByWordsLabel: 'Усього прописом',
      }
    */};

    let itsOk = true;

    from(this.document.items)
      .pipe(switchMap(di => of(di)))
      .subscribe(e => {

        itsOk = itsOk && !e.hlt;
      });

    if (!environment.production || itsOk) {
      this.exportDS = true;
    } else {
      this.confirmExportDocumentText = this.document.DOCNUMBER;
      this.exportConfirmDlg = true;
    }
  }

  /** Sets `exportDS = true` to open the export overlay after the user confirms export despite highlighted warnings. */
  onExportDocumentConfirmation(event) {
    console.log('*** EditDocumentReceiptComponent onExportDocumentConfirmation: ' + JSON.stringify(event))

    this.exportDS = true;
  }

  /** Executes confirmed document deletion and navigates to `/documents` on success. */
  onDeleteDocumentConfirmation(event) {
    console.log('*** EditDocumentReceiptComponent onDeleteDocumentConfirmation: ' + JSON.stringify(event))

    this._deleteDocQuery =
      this.docApi.deleteDocumentById(this.document.DOCID).subscribe(() => {
        console.log('*** EditDocumentReceiptComponent onDeleteDocumentConfirmation: DONE!');
          this.router.navigate(['/documents']);
        });
  }

  /** Guards deletion by checking `getChildDocList`; opens confirmation if no children, or shows an error toast if children exist. */
  deleteDocumentClick(event) {
    console.log('*** EditDocumentReceiptComponent deleteDocumentClick: ' + JSON.stringify(event))

    this._childDocQuery =
      this.docApi.getChildDocList(this._docId).pipe(map(doc => {
        if (doc.length == 0) {
          this.confirmDeleteDocumentText = 'DELETE ' + this.document.DOCNUMBER;
          this.confirmDeleteDocumentOn = true;
        } else {
          this.messageService.add({ severity: 'error', summary: 'Этот документ имеет подчиненные - удалить невозможно.' });
        }
      })).subscribe();
  }

  /** Replaces `document.items` with the imported rows and sets `knockKnock` to trigger a child refresh. */
  onImportDone(event) {
    console.log('*** EditDocumentReceiptComponent onImportDone: ');

    //    environment.production || console.table(event);

    this.document.items = event;
    this.knockKnock = true;
  }
}
